import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc, writeBatch, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { format } from 'date-fns';
import AdminAppBar from './AdminAppBar';
import { FaTimes, FaSearch, FaUser, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaMoneyBillWave, FaCheck, FaBan, FaTrashAlt, FaBoxOpen, FaWhatsapp } from 'react-icons/fa';

const ViewOrdersScreen = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalSales, setTotalSales] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmingOrderId, setConfirmingOrderId] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Fetch and listen for orders in real-time
  useEffect(() => {
    const ordersQuery = collection(db, 'orders');

    const unsubscribe = onSnapshot(ordersQuery, (querySnapshot) => {
      const ordersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Sort orders manually (Newest first)
      const sortedOrders = ordersData.sort((a, b) => {
        // Fallback for date sorting
        const dateA = a.orderDate?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : 0);
        const dateB = b.orderDate?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : 0);
        return dateB - dateA;
      });

      setOrders(sortedOrders);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle Search and Filter
  useEffect(() => {
    if (!orders) return;

    let result = orders;

    // Filter by Status
    if (filterStatus !== 'All') {
      result = result.filter(order => (order.status || '').toLowerCase() === filterStatus.toLowerCase());
    }

    // Filter by Search Query
    if (searchQuery.trim()) {
      const queryLower = searchQuery.toLowerCase();
      result = result.filter(order =>
        (order.customerName && order.customerName.toLowerCase().includes(queryLower)) ||
        (order.customerPhone && order.customerPhone.includes(queryLower)) ||
        (order.customerAddress && order.customerAddress.toLowerCase().includes(queryLower))
      );
    }

    setFilteredOrders(result);
  }, [orders, searchQuery, filterStatus]);

  // Calculate total sales for confirmed orders (Real-time)
  useEffect(() => {
    const confirmedTotal = orders
      .filter(o => (o.status || '').toLowerCase() === 'confirmed')
      .reduce((sum, o) => sum + (Number(o.totalPrice) || 0), 0);
    setTotalSales(confirmedTotal);
  }, [orders]);

  // Confirm an order (change status to "Confirmed")
  const confirmOrder = async (orderId, items) => {
    if (!window.confirm('Are you sure you want to confirm this order? This will update inventory.')) {
      return;
    }

    setConfirmingOrderId(orderId);
    try {
      const batch = writeBatch(db);

      // Update the order status to "Confirmed"
      const orderRef = doc(db, 'orders', orderId);
      batch.update(orderRef, { status: 'Confirmed' });

      // Group items by unique product identity inside the order
      const itemGroups = {};

      items.forEach(item => {
        // Use itemId as primary key if available, otherwise fallback to type_name
        // Robust name handling: fallback to empty string and trim whitespace
        const name = (item.name || item.guppyName || "").trim();

        // The key must use itemId if present to distinctively identify the item in the DB
        // If itemId is missing, we fallback to our grouping key based on name
        const key = item.itemId ? `ID_${item.itemId}` : `${item.type}_${name}`;

        if (!itemGroups[key]) {
          itemGroups[key] = {
            itemId: item.itemId,
            type: item.type,
            name: name,
            items: []
          };
        }
        itemGroups[key].items.push(item);
      });

      // Process each unique product
      for (const key in itemGroups) {
        const group = itemGroups[key];
        const { itemId, type, name, items: groupItems } = group;

        // Determine collection
        let collectionName = '';
        if (type === 'guppy') collectionName = 'guppies';
        else if (type === 'plant') collectionName = 'plants';
        else if (type === 'aquarium') collectionName = 'aquariums';
        else if (type === 'food') collectionName = 'food';

        if (!collectionName) continue;

        let docRef;
        let data;

        // Strategy 1: Lookout by ID (Best/New Orders)
        if (itemId) {
          const idQuery = query(collection(db, collectionName), where('__name__', '==', itemId));
          const idSnapshot = await getDocs(idQuery);

          if (!idSnapshot.empty) {
            const docSnapshot = idSnapshot.docs[0];
            docRef = doc(db, collectionName, docSnapshot.id);
            data = docSnapshot.data();
          } else {
            console.warn(`Item ID ${itemId} not found in collection ${collectionName}`);
          }
        }

        // Strategy 2: Fallback to Name Lookup (Legacy Orders or if ID lookup failed)
        if (!docRef) {
          // Try exact match first
          const nameQuery = query(collection(db, collectionName), where('name', '==', name));
          const nameSnapshot = await getDocs(nameQuery);

          if (!nameSnapshot.empty) {
            const docSnapshot = nameSnapshot.docs[0];
            docRef = doc(db, collectionName, docSnapshot.id);
            data = docSnapshot.data();
          } else {
            console.warn(`Item not found in DB: "${name}" (Type: ${type}, ID: ${itemId})`);
            continue;
          }
        }

        // Calculate and Apply Updates based on Type
        // Helper to safely parse numbers, defaulting to 0 if NaN or undefined
        const safeNum = (val) => {
          const n = Number(val);
          return isNaN(n) ? 0 : n;
        };

        if (type === 'guppy') {
          let totalOrderCouple = 0;
          let totalOrderFemale = 0;
          let totalOrderMale = 0;

          // Aggregate quantities from all matching items in this order
          groupItems.forEach(item => {
            totalOrderCouple += safeNum(item.coupleCount || item.couplecount);
            totalOrderFemale += safeNum(item.femaleCount || item.femalecount);
            totalOrderMale += safeNum(item.maleCount || item.malecount);
          });

          const currentCouple = safeNum(data.couplecount);
          const currentFemale = safeNum(data.femalecount);
          const currentMale = safeNum(data.malecount);

          const newCouple = Math.max(0, currentCouple - totalOrderCouple);
          const newFemale = Math.max(0, currentFemale - totalOrderFemale);
          const newMale = Math.max(0, currentMale - totalOrderMale);

          batch.update(docRef, {
            couplecount: newCouple,
            femalecount: newFemale,
            malecount: newMale,
            sold_status: (newCouple <= 0 && newFemale <= 0 && newMale <= 0)
          });
        }
        else if (type === 'food') {
          let totalOrderPowder = 0;
          let totalOrderFlakes = 0;

          groupItems.forEach(item => {
            const qty = safeNum(item.quantity);
            const fType = item.foodType || 'flakes';
            if (fType === 'powder') totalOrderPowder += qty;
            else totalOrderFlakes += qty;
          });

          const currentPowder = safeNum(data.powderQuantity);
          const currentFlakes = safeNum(data.flakesQuantity);

          const newPowder = Math.max(0, currentPowder - totalOrderPowder);
          const newFlakes = Math.max(0, currentFlakes - totalOrderFlakes);

          batch.update(docRef, {
            powderQuantity: newPowder,
            flakesQuantity: newFlakes,
            sold_status: (newPowder <= 0 && newFlakes <= 0)
          });
        }
        else {
          // Plant or Aquarium (Simple Quantity)
          let totalOrderQty = 0;
          groupItems.forEach(item => {
            totalOrderQty += safeNum(item.quantity);
          });

          const currentQty = safeNum(data.quantity);
          const newQty = Math.max(0, currentQty - totalOrderQty);

          batch.update(docRef, {
            quantity: newQty,
            sold_status: (newQty <= 0)
          });
        }
      }

      await batch.commit();

      // Send the webhook payload to n8n to generate the receipt
      try {
        const orderToConfirm = orders.find(o => o.id === orderId) || { id: orderId };
        const payload = {
          ...orderToConfirm,
          status: "Confirmed"
        };

        const webhookUrl = process.env.REACT_APP_N8N_CONFIRM_WEBHOOK_URL;
        await fetch(webhookUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload)
        });
        console.log("Confirmation webhook sent successfully");
      } catch (webhookError) {
        console.error("Failed to send order confirmation webhook to n8n:", webhookError);
      }

      alert('Order confirmed and inventory updated successfully!');
    } catch (error) {
      console.error('Error confirming order:', error);
      alert(`Failed to confirm order: ${error.message || 'Unknown error'}`);
    } finally {
      setConfirmingOrderId(null);
    }
  };

  // Cancel an order
  const cancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: 'Cancelled' });
      alert('Order cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert(`Failed to cancel order: ${error.message || 'Unknown error'}`);
    }
  };

  // Delete an order
  const deleteOrder = async (orderId) => {
    if (showDeleteConfirm !== orderId) {
      setShowDeleteConfirm(orderId);
      return;
    }
    setDeletingOrderId(orderId);
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      alert('Order deleted successfully!');
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert(`Failed to delete order: ${error.message || 'Unknown error'}`);
    } finally {
      setDeletingOrderId(null);
    }
  };

  const openWhatsApp = (phone) => {
    if (!phone) return;
    let formattedPhone = phone.replace(/\\D/g, '');
    if (formattedPhone.startsWith('0')) {
      formattedPhone = '213' + formattedPhone.substring(1);
    }
    window.open(`https://wa.me/${formattedPhone}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return '#10b981';
      case 'Pending': return '#f59e0b';
      case 'Cancelled': return '#ef4444';
      case 'CancelledByClient': return '#64748b';
      default: return '#cbd5e1';
    }
  };

  // Render item details based on item type
  const renderItemDetails = (item, itemIndex) => {
    const itemStyle = {
      background: 'rgba(30, 41, 59, 0.4)',
      borderRadius: '12px',
      padding: '16px',
      border: '1px solid rgba(148, 163, 184, 0.1)',
      marginBottom: '12px'
    };

    switch (item.type) {
      case 'plant':
        return (
          <div key={itemIndex} style={itemStyle}>
            <h4 style={{ color: '#22c55e', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700' }}>🌿 Plant: {item.name}</h4>
            <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'grid', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Type: {item.plantType || 'Standard'}</span>
                <span>Qty: <strong>{item.quantity}</strong></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Unit: {item.price} DA</span>
                <span style={{ color: '#fff', fontWeight: '600' }}>Total: {(item.price * item.quantity).toFixed(2)} DA</span>
              </div>
            </div>
            {item.care && <div style={{ marginTop: '8px', fontSize: '12px', color: '#94a3b8' }}>Care: {item.care}</div>}
          </div>
        );

      case 'aquarium':
        return (
          <div key={itemIndex} style={itemStyle}>
            <h4 style={{ color: '#3b82f6', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700' }}>💧 Aquarium: {item.name}</h4>
            <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'grid', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Material: {item.material || 'N/A'}</span>
                <span>Qty: <strong>{item.quantity}</strong></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Size: {item.size || 'N/A'}</span>
                <span style={{ color: '#fff', fontWeight: '600' }}>Total: {(item.price * item.quantity).toFixed(2)} DA</span>
              </div>
            </div>
          </div>
        );

      case 'guppy':
        return (
          <div key={itemIndex} style={itemStyle}>
            <h4 style={{ color: '#06b6d4', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700' }}>🐠 Guppy: {item.guppyName || item.name}</h4>
            <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'grid', gap: '8px' }}>
              {item.coupleCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '4px' }}>
                  <span>Couples</span>
                  <span><strong>{item.coupleCount}</strong> x {item.couplePrice} DA</span>
                </div>
              )}
              {item.femaleCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '4px' }}>
                  <span>Females</span>
                  <span><strong>{item.femaleCount}</strong> x {item.femalePrice} DA</span>
                </div>
              )}
              {item.maleCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Males</span>
                  <span><strong>{item.maleCount}</strong> x {item.malePrice} DA</span>
                </div>
              )}
              {item.guppyNumber && <div style={{ fontSize: '12px', color: '#64748b' }}>Reference: #{item.guppyNumber}</div>}
            </div>
          </div>
        );

      case 'food':
        const isFishFood = true; // Simplified for new UI
        const weight = item.weightInGrams || (item.quantity * 50);
        return (
          <div key={itemIndex} style={itemStyle}>
            <h4 style={{ color: '#f97316', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700' }}>🍟 Food: {item.name}</h4>
            <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'grid', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Type: {item.form || (item.foodType === 'powder' ? 'Powder' : 'Flakes')}</span>
                <span>Qty: <strong>{weight}g</strong></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Price/50g: {item.basePricePer50g} DA</span>
                <span style={{ color: '#fff', fontWeight: '600' }}>Total: {item.price || (item.basePricePer50g * item.quantity)} DA</span>
              </div>
            </div>
          </div>
        );

      default:
        // Generic fallback
        return (
          <div key={itemIndex} style={itemStyle}>
            <h4 style={{ color: '#94a3b8', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700' }}>📦 Item: {item.name || 'Unknown'}</h4>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>
              Price: {item.price} DA | Qty: {item.quantity}
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e1e 0%, #1a1f2e 25%, #0f172a 50%, #1a1f2e 75%, #0a0e1e 100%)',
      backgroundSize: '400% 400%',
      position: 'relative',
      width: '100%',
      overflowX: 'hidden',
      animation: 'gradientShift 15s ease infinite',
      color: '#e2e8f0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      paddingBottom: '40px'
    }}>
      {/* Animated Particle Background */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        opacity: 0.15,
        pointerEvents: 'none'
      }}>
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              background: `hsl(${200 + Math.random() * 60}, 70%, ${60 + Math.random() * 20}%)`,
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <AdminAppBar />

      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px', paddingTop: '20px' }}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: '900',
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 50%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            filter: 'drop-shadow(0 0 30px rgba(16, 185, 129, 0.2))'
          }}>
            Orders Managment
          </h1>
          <div style={{
            width: '100px',
            height: '4px',
            background: 'linear-gradient(90deg, transparent, #10b981, #06b6d4, transparent)',
            margin: '0 auto',
            borderRadius: '2px'
          }}></div>
        </div>

        {/* Sales Stats Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '24px',
          padding: '24px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(16, 185, 129, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#10b981'
            }}>
              <FaMoneyBillWave size={28} />
            </div>
            <div>
              <div style={{ fontSize: '14px', color: '#94a3b8' }}>Total Confirmed Sales</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#fff' }}>{totalSales.toLocaleString()} DA</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ padding: '8px 16px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '12px', color: '#10b981', fontWeight: '600' }}>
              {orders.filter(o => o.status === 'Confirmed').length} Completed
            </div>
            <div style={{ padding: '8px 16px', background: 'rgba(245, 158, 11, 0.2)', borderRadius: '12px', color: '#f59e0b', fontWeight: '600' }}>
              {orders.filter(o => o.status === 'Pending').length} Pending
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          {/* Search Bar */}
          <div style={{ position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search customer, phone, address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 16px 16px 48px',
                background: 'rgba(30, 41, 59, 0.6)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '16px',
                color: 'white',
                fontSize: '16px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(71, 85, 105, 0.5)'}
            />
          </div>

          {/* Filter Dropdown */}
          <div style={{ position: 'relative' }}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '16px',
                background: 'rgba(30, 41, 59, 0.6)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '16px',
                color: 'white',
                fontSize: '16px',
                outline: 'none',
                cursor: 'pointer',
                appearance: 'none',
                backdropFilter: 'blur(10px)'
              }}
            >
              <option value="All">All Statuses</option>
              <option value="Pending">Pending Only</option>
              <option value="Confirmed">Confirmed Only</option>
              <option value="Cancelled">Cancelled</option>
              <option value="CancelledByClient">Client Cancelled</option>
            </select>
            <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}>▼</div>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading orders...</div>
        ) : filteredOrders.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            background: 'rgba(30, 41, 59, 0.3)',
            borderRadius: '24px',
            border: '1px dashed rgba(71, 85, 105, 0.5)'
          }}>
            <FaBoxOpen size={48} color="#64748b" style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ color: '#e2e8f0', fontSize: '18px' }}>No orders found</h3>
            <p style={{ color: '#94a3b8' }}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '24px' }}>
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                style={{
                  background: 'rgba(30, 41, 59, 0.7)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${getStatusColor(order.status)}50`,
                  borderRadius: '24px',
                  padding: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
              >
                {/* Status Stripe */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: '4px',
                  background: getStatusColor(order.status)
                }}></div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '24px' }}>

                  {/* Customer Info */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#fff', margin: 0 }}>
                        {order.customerName || 'Unknown Customer'}
                      </h2>
                      <span style={{
                        fontSize: '12px',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        background: `${getStatusColor(order.status)}20`,
                        color: getStatusColor(order.status),
                        border: `1px solid ${getStatusColor(order.status)}40`,
                        fontWeight: '700'
                      }}>
                        {order.status}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#cbd5e1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaPhone size={14} color="#64748b" />
                        <span>{order.customerPhone}</span>
                        {order.customerPhone && (
                          <button
                            onClick={() => openWhatsApp(order.customerPhone)}
                            title="Message on WhatsApp"
                            style={{
                              background: '#25D366',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              color: 'white',
                              marginLeft: '8px',
                              boxShadow: '0 2px 5px rgba(37, 211, 102, 0.4)'
                            }}
                          >
                            <FaWhatsapp size={14} />
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaMapMarkerAlt size={14} color="#64748b" />
                        <span>{order.customerAddress}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaCalendarAlt size={14} color="#64748b" />
                        <span>
                          {order.orderDate 
                            ? format(order.orderDate.toDate(), 'dd MMM yyyy, hh:mm a') 
                            : order.createdAt 
                              ? format(new Date(order.createdAt), 'dd MMM yyyy, hh:mm a')
                              : 'Date not available'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Info */}
                  <div style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    borderRadius: '16px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '4px' }}>Total Amount</div>
                    <div style={{ fontSize: '32px', fontWeight: '800', color: '#10b981', letterSpacing: '-1px' }}>
                      {order.totalPrice ? order.totalPrice.toLocaleString() : 0} <span style={{ fontSize: '16px' }}>DA</span>
                    </div>
                    {order.comment && (
                      <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', fontSize: '13px', color: '#fbbf24' }}>
                        Note: {order.comment}
                      </div>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: 'rgba(148, 163, 184, 0.1)', marginBottom: '20px' }}></div>

                {/* Items List */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaBoxOpen /> Ordered Items ({order.items ? order.items.length : 0})
                  </h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                    {order.items && order.items.map((item, idx) => renderItemDetails(item, idx))}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'flex-end' }}>
                  {(order.status || '').toLowerCase() === 'pending' && (
                    <button
                      onClick={() => confirmOrder(order.id, order.items)}
                      disabled={confirmingOrderId === order.id}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: '12px',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: confirmingOrderId === order.id ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                      }}
                    >
                      <FaCheck /> {confirmingOrderId === order.id ? 'Processing...' : 'Confirm Order'}
                    </button>
                  )}

                  {((order.status || '').toLowerCase() === 'pending' || (order.status || '').toLowerCase() === 'confirmed') && (
                    <button
                      onClick={() => cancelOrder(order.id)}
                      style={{
                        padding: '12px 24px',
                        background: 'rgba(71, 85, 105, 0.3)',
                        border: '1px solid rgba(148, 163, 184, 0.3)',
                        borderRadius: '12px',
                        color: '#e2e8f0',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <FaBan /> Cancel
                    </button>
                  )}

                  <button
                    onClick={() => deleteOrder(order.id)}
                    disabled={deletingOrderId === order.id}
                    style={{
                      padding: '12px 24px',
                      background: showDeleteConfirm === order.id ? '#ef4444' : 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid #ef4444',
                      borderRadius: '12px',
                      color: showDeleteConfirm === order.id ? 'white' : '#ef4444',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FaTrashAlt /> {showDeleteConfirm === order.id ? 'Click to Confirm' : 'Delete'}
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOrdersScreen;